import React, {useCallback} from "react";
import styled from "@emotion/styled";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {getSquadById} from "modules/selectors";
import {IS_PLAYER_MODAL_DISABLED} from "modules/constants";
import {openPlayerDetailsModal} from "modules/actions";
import {getPlayerFullName, getPoolStats, getSquadKey, getStatsValue} from "modules/utils";
import {getPlayerAvatarSrc, getPlayerName, toLowerCase} from "modules/utils/helpers";
import {IPlayer} from "modules/types";
import {PlayerAvatar} from "components";

const Wrapper = styled.div`
	font-family: ${({theme}) => theme.baseFont};
	border-bottom: 1px solid ${({theme}) => theme.colorPalette.black};
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: #545456;
	color: ${({theme}) => theme.colorPalette.white};
	padding: 11px 12px 10px 16px;
	text-align: left;
	border-radius: 0;
	cursor: pointer;

	&:hover {
		background: #a408a4;
	}
`;

const LeftBlock = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	height: 100%;
`;

const PlayerInfo = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	height: 100%;
`;

const PlayerName = styled.div`
	font-size: 14px;
	font-weight: 700;
	line-height: 16px;
	text-decoration: underline;
`;

const PlayerPosition = styled.div`
	font-family: "Lato", sans-serif;
	font-size: 14px;
	font-weight: 400;
	line-height: 16px;
`;

const PlayerStats = styled(PlayerPosition)`
	display: flex;
	flex-wrap: wrap;
	column-gap: 8px;
	margin-top: 12px;

	b {
		font-weight: 700;
	}
`;

interface IProps {
	readonly style: object;
	player: IPlayer;
	PlayerAction: React.FC<{player: IPlayer; isDisabled: boolean}>;
	isDisabled: boolean;
}

export const PlayerPoolRow: React.FC<IProps> = ({style, player, PlayerAction, isDisabled}) => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const squad = useSelector(getSquadById)(player.squadId);

	const handlePlayerClick = useCallback(() => {
		if (IS_PLAYER_MODAL_DISABLED) {
			return;
		}

		dispatch(openPlayerDetailsModal({id: player.id}));
	}, [dispatch, player]);

	return (
		<Wrapper onClick={handlePlayerClick} style={style}>
			<LeftBlock>
				<PlayerAvatar src={getPlayerAvatarSrc(player)} alt={getPlayerName(player)} />

				<PlayerInfo>
					<PlayerName>{getPlayerFullName(player)}</PlayerName>
					<PlayerPosition>
						{t(getSquadKey(squad))} |{" "}
						{t(toLowerCase(`game.position.${player.position}`))}
					</PlayerPosition>

					<PlayerStats>
						{getPoolStats(player).map(({value, key, isShowDecimal}) => (
							<div key={key}>
								<b>{t(key)}</b> | {getStatsValue(value, isShowDecimal)}
							</div>
						))}
					</PlayerStats>
				</PlayerInfo>
			</LeftBlock>

			<PlayerAction player={player} isDisabled={isDisabled} />
		</Wrapper>
	);
};
